import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { RouterProvider } from '@tanstack/react-router';
import { AuthProvider } from './auth/AuthContext.tsx';
import { createAppRouter } from './router.tsx';
import { SquadProvider } from './state/SquadProvider.tsx';
import { InstallPrompt } from './components/InstallPrompt.tsx';
import { initAnalytics } from './lib/analytics.ts';
import { registerServiceWorker } from './lib/serviceWorker.ts';
import { initSentry } from './lib/sentry.ts';
import './styles.css';

// Both are no-ops until their respective VITE_*_KEY is set at build time —
// see lib/sentry.ts and lib/analytics.ts.
initSentry();
initAnalytics();
// No-op in dev, or wherever service workers aren't supported — see
// lib/serviceWorker.ts.
registerServiceWorker();

const root = document.getElementById('root');
if (!root) throw new Error('index.html is missing the #root element');

const router = createAppRouter();

/** A crash the app can't recover from — same visual language as the rest of
 * the app rather than Sentry's default fallback, which would look like a
 * completely different product mid-crash.
 *
 * Deliberately no `SkipLink` here (T-102): every other shell has real
 * navigation and a masthead above `<main>` worth jumping past; this one is
 * a single error panel with nothing above it and one button inside it — a
 * skip link would have nowhere useful to send focus that tabbing forward
 * once wouldn't already reach. */
function CrashFallback() {
  return (
    <div className="shell">
      <main>
        <section className="panel panel--bad" aria-label="Something went wrong">
          <h3>Something went wrong</h3>
          <p className="hint">
            This screen hit an error we couldn't recover from. Reloading usually fixes it — if it
            keeps happening, it's on us, not something you did.
          </p>
          <button type="button" onClick={() => window.location.reload()}>
            Reload
          </button>
        </section>
      </main>
    </div>
  );
}

createRoot(root).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<CrashFallback />}>
      <AuthProvider>
        {/* Mounted above the router (T-048) so every route — squad desk,
         * fixtures, tracker — can read the loaded squad. */}
        <SquadProvider>
          <RouterProvider router={router} />
        </SquadProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
    {/* Outside the ErrorBoundary and independent of auth/route state, same
     * reason CookieBanner sits outside App()'s branches (T-109 doesn't own
     * App.tsx, or this would live there instead) — installability isn't
     * tied to being signed in or to which screen crashed. */}
    <InstallPrompt />
  </StrictMode>,
);
